// Tutorial: 5 paginated slides shown once before the player's first game.
// Skipped on Replay via a sessionStorage flag set when the user clicks BEGIN.
// BGM is untouched here — the loadscreen track started in BootScene keeps
// playing through Intro + Tutorial until GameScene cross-fades to bgmMain.

import Phaser from 'phaser';
import { GameParams } from '../params';

const TOTAL_SLIDES = 5;
export const TUTORIAL_SEEN_KEY = 'zdc:seenTutorial';

export class TutorialScene extends Phaser.Scene {
  private params!: GameParams;

  constructor() {
    super('Tutorial');
  }

  init(data: { params: GameParams }) {
    this.params = data.params;
  }

  create() {
    const overlay = document.getElementById('tutorial-overlay');
    if (!overlay) {
      // No overlay markup — fail open straight into gameplay.
      this.scene.start('Game', { params: this.params });
      return;
    }
    overlay.classList.remove('hidden');

    const slides = overlay.querySelectorAll<HTMLElement>('.ix-tut-slide');
    const pips = overlay.querySelectorAll<HTMLElement>('.ix-tut-pip');
    const eyebrowTag = document.getElementById('tut-eyebrow-tag');
    const eyebrow = overlay.querySelector<HTMLElement>('.ix-tut-eyebrow');
    const stepNumEl = document.getElementById('tut-step-num');
    const nextBtn = document.getElementById('tut-next-btn');
    const backBtn = document.getElementById('tut-back-btn');

    let step = 1;
    const render = () => {
      slides.forEach((el, i) => el.classList.toggle('active', i === step - 1));
      pips.forEach((el, i) => el.classList.toggle('active', i === step - 1));
      overlay.setAttribute('data-step', String(step));

      // Eyebrow: steps 1–4 read "STEP NN · HOW TO PLAY" (blue), step 5
      // reads "WATCH OUT · HAZARDS" (red).
      if (eyebrowTag) {
        if (step === TOTAL_SLIDES) {
          eyebrowTag.innerHTML = 'WATCH OUT &middot; HAZARDS';
        } else {
          eyebrowTag.innerHTML = `STEP <span id="tut-step-num">${String(step).padStart(2, '0')}</span> &middot; HOW TO PLAY`;
        }
      }
      eyebrow?.classList.toggle('danger', step === TOTAL_SLIDES);
      if (stepNumEl && step !== TOTAL_SLIDES) stepNumEl.textContent = String(step).padStart(2, '0');

      if (nextBtn) {
        nextBtn.innerHTML = step === TOTAL_SLIDES ? 'BEGIN ▸' : 'NEXT ▸';
      }
    };
    render();

    // AbortController so listeners die with the scene — same pattern as
    // IntroScene / EndScene.
    const ac = new AbortController();
    const finish = () => {
      try { sessionStorage.setItem(TUTORIAL_SEEN_KEY, '1'); } catch { /* ignore */ }
      overlay.classList.add('hidden');
      this.scene.start('Game', { params: this.params });
    };
    const advance = () => {
      if (step < TOTAL_SLIDES) {
        step++;
        render();
        return;
      }
      finish();
    };
    const back = () => {
      if (step === 1) {
        // Step 1: the button reads SKIP — bail out of the tutorial entirely.
        finish();
        return;
      }
      step--;
      render();
    };

    nextBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      advance();
    }, { signal: ac.signal });

    backBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      back();
    }, { signal: ac.signal });

    this.input.keyboard?.on('keydown', (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'Backspace') back();
      else advance();
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      ac.abort();
      overlay.classList.add('hidden');
      overlay.removeAttribute('data-step');
    });
  }
}
